import { useQuery } from "@tanstack/react-query";
import { getSubjects } from "api/subject.api.ts";
import { useFilters } from "context/FilterContext";
import { onlineTypeOptions } from "conts/options";
import { filterAllowedClasses } from "conts/allowedClasses";
import { useTypewriter } from "hooks/useTypeWriter";
import { userRoute } from "pages/user/user.routes";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { AutoCompleteInputVariant } from "ui/base/autoCompleteInput/AutoCompleteInputVariant";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { SelectButton } from "ui/base/selectInput/SelectButton";
import { SelectFilter } from "ui/base/selectInput/SelectFilter";
import { Tooltip } from "ui/base/tooltip/Tooltip";
import { SearchIcon } from "ui/icons";
import { defaultThemeColors, fonts } from "ui/theme";
import { getFiltersSearchDisabledMessage, isFiltersSearchDisabled } from "./filterUtils";

const typewriterAnimationClasses = [
  "Guitar",
  "Piano",
  "Singing",
  "Trumpet",
  "Sound Therapy",
  "Violin",
  "Drums",
  "DJing",
  "Music Production",
  "Songwriting",
  "Music Theory",
  "Bass Guitar",
  "Ukulele",
  "Saxophone",
  "Flute",
  "Composition",
  "Beat Making",
  "Audio Engineering",
  "Mixing & Mastering",
  "Dancing",
];

export const FiltersSection = ({ onSearch }) => {
  const navigate = useNavigate();
  const { filters, setFilters, handleLocationChange } = useFilters();

  const { data: subjectData } = useQuery({
    queryKey: ["subjects"],
    queryFn: getSubjects,
  });

  const filteredSubjects = filterAllowedClasses(subjectData);
  
  const subjects =
    filteredSubjects?.map((subject) => ({
      value: subject.id,
      label: subject.title,
    })) || [];

  const online_type = filters.online_type?.value || "";
  const disabledMessage = getFiltersSearchDisabledMessage(filters);

  const isMobile = useMediaQuery({ minWidth: 768 });



  if (!isMobile) return null;

  return (
    <Wrapper>
      <Container isLocationVisible={online_type === "offline"}>
        <div className="filters">
          <TypeOfClassContainer isLocationVisible={online_type === "offline"}>
            <TypewriterSelectFilter
              data-testid="home-search-type-of-class-select"
              value={filters.typeOfClass}
              onChange={(subject) => {
                setFilters({
                  typeOfClass: subject,
                });
              }}
              options={subjects}
            />
          </TypeOfClassContainer>

          <LessonTypeContainer isLocationVisible={online_type === "offline"}>
            <SelectButton
              data-testid="home-search-lesson-type-select"
              placeholder="Select Lesson Type"
              bgColor="white"
              borderColor="txt8PercentOpacity"
              valueColor="txt"
              placeholderColor="txt"
              chevronColor="#191D3A"
              height="40px"
              rounded
              value={filters.online_type}
              onChange={(lesson) =>
                setFilters({
                  online_type: lesson,
                  location: "",
                  coordinates: null,
                })
              }
              options={onlineTypeOptions}
            />
          </LessonTypeContainer>

          <LocationContainer isVisible={online_type === "offline"}>
            <AutoCompleteInputVariant 
              data-testid="home-search-location-input"
              borderColor="lightBlue"
              placeholder="Enter the city"
              placeholderColor="txt"
              bgColor="transparent"
              // optionType="region"
              rounded
              address={
                filters.location
                  ? { label: filters.location, value: filters.location }
                  : null
              }
              onChange={handleLocationChange}
            />
          </LocationContainer>
        </div>
        <div>
          <Tooltip
            id="search-button-tooltip"
            text={disabledMessage}
            place="top"
            display="block"
          >
            <ButtonPrimaryStyled
              variant="icon_rounded"
              disabled={isFiltersSearchDisabled(filters)}
              onClick={() => {
                navigate(userRoute.searchOnline, {
                  state: filters,
                });
                onSearch?.();
              }}
            >
              <SearchIcon style={{ width: "18px", height: "18px" }} />
            </ButtonPrimaryStyled>
          </Tooltip>
        </div>
      </Container>
    </Wrapper>
  );
};

export const TypewriterSelectFilter = ({ value, onChange, options }) => {
  const { pathname } = useLocation();

  const isHomePage = pathname === "/" || pathname === "";

  const {
    text: typedText,
    pause,
    resume,
  } = useTypewriter(typewriterAnimationClasses, {
    enabled: isHomePage,
    pauseOnFocus: true,
  });

  const handleFocus = () => pause();
  const handleBlur = () => {
    if (!value) resume();
  };

  return (
    <SelectFilter
      borderColor="txt8PercentOpacity"
      bgColor="white"
      rounded
      placeholder=""
      animatedPlaceholder={isHomePage ? `Learn ${typedText}` : "Type a class"}
      value={value}
      onChange={onChange}
      options={options}
      onFocus={handleFocus}
      onBlur={handleBlur}
      noOptionsMessage={() => null}
    />
  );
};

const fadeIn = keyframes`
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const Container = styled.div`
  width: 100%;
  max-width: 685px;
  height: auto;
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${(props) => (props.isLocationVisible ? "1rem" : "0")};
  background-color: ${defaultThemeColors.secondary};
  margin-inline: auto;

  & > .filters {
    width: 100%;
    display: flex;
    min-width: 300px;
    align-items: center;
    align-content: center;
    flex-wrap: wrap;
    gap: ${(props) => (props.isLocationVisible ? "1rem" : "1rem")};
  }

  @media (width >= 48rem) {
    border-radius: 99rem;
    & > .filters {
      flex-wrap: nowrap;
    }
  }

  @media (min-width: 1024px) {
    min-width: 540px;
  }
`;

const TypeOfClassContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: fit-content;
  --auto-complete-font-size: ${fonts.sizes.small};
  @media (width >= 48rem) {
    max-width: ${(props) => (props.isLocationVisible ? "240px" : "397px")};
  }
`;

const LessonTypeContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: fit-content;
  --auto-complete-font-size: ${fonts.sizes.small};
  font-size: ${fonts.sizes.small};
  @media (width >= 48rem) {
    max-width: ${(props) => (props.isLocationVisible ? "200px" : "345px")};
  }
`;

const LocationContainer = styled.div`
  --auto-complete-font-size: ${fonts.sizes.small};
  width: ${(props) => (props.isVisible ? "100%" : "0")};
  overflow: ${(props) => (props.isVisible ? "visible" : "hidden")};
  animation: ${(props) =>
    props.isVisible
      ? css`
          none
        `
      : "none"};

  & > * {
    max-width: 100%;
    @media (width >= 48rem) {
      max-width: 380px;
    }
  }
`;

const ButtonPrimaryStyled = styled(ButtonPrimary)`
  height: 40px;
  width: 40px;
`;
